import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  House, User, Clock, ChatCircle, DotsThree,
  Users, BookOpen, Scroll, X, Gear, Bell,
  CaretRight
} from "@phosphor-icons/react";

const PRIMARY_NAV = [
  { path: "/home", icon: House, label: "Home" },
  { path: "/my-life", icon: User, label: "My Life" },
  { path: "/timing", icon: Clock, label: "Timing" },
  { path: "/ask", icon: ChatCircle, label: "Ask" },
];

const MORE_ITEMS = [
  { path: "/relationships", icon: Users, label: "Relationships & Family", desc: "Compatibility, family dynamics" },
  { path: "/learn-grow", icon: BookOpen, label: "Learn & Grow", desc: "Courses, kids' stories, mantras" },
  { path: "/reports", icon: Scroll, label: "Reports & Deep Dives", desc: "Personalized astrology reports" },
  { path: "/notifications", icon: Bell, label: "Notifications", desc: "Alerts and transit updates" },
  { path: "/settings", icon: Gear, label: "Settings", desc: "Profile, birth chart, preferences" },
];

const Navigation = () => {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const isActive = (path) => {
    if (path === "/home") {
      return location.pathname === "/home" || location.pathname === "/dashboard" || location.pathname === "/today" || location.pathname === "/";
    }
    if (path === "/my-life") {
      return location.pathname.startsWith("/my-life") || location.pathname === "/insights" || location.pathname === "/kundli" || location.pathname === "/horoscope" || location.pathname === "/lifepath" || location.pathname === "/life-readings" || location.pathname.startsWith("/report/") || location.pathname === "/dasha-explorer" || location.pathname === "/vedic-depth" || location.pathname === "/knowledge" || location.pathname === "/habits" || location.pathname === "/remedies" || location.pathname === "/ekadashi";
    }
    if (path === "/timing") {
      return location.pathname.startsWith("/timing") || location.pathname === "/muhurta" || location.pathname === "/panchang" || location.pathname === "/prashna-varshphal";
    }
    if (path === "/relationships") {
      return location.pathname.startsWith("/relationships") || location.pathname === "/family" || location.pathname.startsWith("/family") || location.pathname === "/compatibility" || location.pathname === "/marriage-compatibility" || location.pathname === "/family-dashboard";
    }
    if (path === "/ask") {
      return location.pathname === "/ask" || location.pathname.startsWith("/ask/");
    }
    if (path === "/learn-grow") {
      return location.pathname.startsWith("/learn") || location.pathname === "/kids-courses" || location.pathname.startsWith("/kids-courses") || location.pathname === "/mantras" || location.pathname === "/guided-rituals";
    }
    if (path === "/reports") {
      return location.pathname === "/reports" || location.pathname === "/report-repository" || location.pathname.startsWith("/reports/") || location.pathname === "/reports-catalog";
    }
    return location.pathname.startsWith(path);
  };

  const isMoreActive = MORE_ITEMS.some(item => isActive(item.path));

  return (
    <>
      <nav className="nav-dock" data-testid="main-navigation" role="navigation" aria-label="Main navigation">
        <div className="nav-dock-inner">
          {PRIMARY_NAV.map(({ path, icon: Icon, label }) => {
            const active = isActive(path);
            return (
              <Link
                key={path}
                to={path}
                className={`nav-link ${active ? "active" : ""}`}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                aria-label={`Navigate to ${label}`}
                aria-current={active ? "page" : undefined}
              >
                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} className="nav-icon-wrapper">
                  <Icon size={22} weight={active ? "fill" : "regular"} className={active ? "text-white" : "text-[var(--text-muted)]"} />
                </motion.div>
                <span className={`nav-label ${active ? "text-white" : "text-[var(--text-muted)]"}`}>{label}</span>
                {active && (
                  <motion.div layoutId="nav-indicator" className="nav-active-indicator" transition={{ type: "spring", stiffness: 300, damping: 30 }} />
                )}
              </Link>
            );
          })}

          {/* More Button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={`nav-link ${isMoreActive ? "active" : ""}`}
            data-testid="nav-more"
            aria-label="More sections"
          >
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} className="nav-icon-wrapper">
              <DotsThree size={22} weight={isMoreActive ? "fill" : "bold"} className={isMoreActive ? "text-white" : "text-[var(--text-muted)]"} />
            </motion.div>
            <span className={`nav-label ${isMoreActive ? "text-white" : "text-[var(--text-muted)]"}`}>More</span>
            {isMoreActive && (
              <motion.div layoutId="nav-indicator" className="nav-active-indicator" transition={{ type: "spring", stiffness: 300, damping: 30 }} />
            )}
          </button>
        </div>
      </nav>

      {/* More Bottom Sheet */}
      <AnimatePresence>
        {moreOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-[10000]"
              onClick={() => setMoreOpen(false)}
              data-testid="more-overlay"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-[10001] bg-[var(--bg-surface)] rounded-t-2xl shadow-2xl overflow-hidden"
              data-testid="more-sheet"
            >
              <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <h3 className="text-base font-medium text-[var(--text-primary)]">More Sections</h3>
                <button
                  onClick={() => setMoreOpen(false)}
                  className="p-2 rounded-lg hover:bg-[var(--bg-muted)] transition-colors"
                  data-testid="more-close-btn"
                  aria-label="Close menu"
                >
                  <X size={18} className="text-[var(--text-muted)]" />
                </button>
              </div>
              <div className="px-3 pb-6 space-y-1" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
                {MORE_ITEMS.map(({ path, icon: Icon, label, desc }) => {
                  const active = isActive(path);
                  return (
                    <Link
                      key={path}
                      to={path}
                      onClick={() => setMoreOpen(false)}
                      className={`flex items-center gap-3.5 px-3 py-3 rounded-xl transition-colors ${active ? "bg-[var(--truffle)] text-white" : "hover:bg-[var(--bg-muted)]"}`}
                      data-testid={`more-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? "bg-white/20" : "bg-[var(--bg-muted)]"}`}>
                        <Icon size={20} weight={active ? "fill" : "duotone"} className={active ? "text-white" : "text-[var(--truffle)]"} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${active ? "text-white" : "text-[var(--text-primary)]"}`}>{label}</p>
                        <p className={`text-xs ${active ? "text-white/70" : "text-[var(--text-muted)]"}`}>{desc}</p>
                      </div>
                      <CaretRight size={16} className={active ? "text-white/50" : "text-[var(--text-muted)]"} />
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
